// AuthProvider.js (COMPLETE FIXED VERSION WITH SILENT REFRESH + REDIRECT PRESERVATION)
import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // ✅ Ref to hold the silent-refresh timer so we can clear it on logout
  const refreshTimerRef = useRef(null);

  // ============================================
  // EXISTING HELPERS (UNCHANGED)
  // ============================================

  const getApiBaseUrl = () => {
    return 'https://hms.backend.hiragroupcloud.com';
  };

  const getCurrentSubdomain = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    if (parts.length >= 4 && parts[parts.length - 2] === 'hms' && parts[parts.length - 1] === 'hiragroupcloud.com') {
      return parts[0];
    }

    if (parts.length > 2 && !['localhost', '127.0.0.1'].includes(hostname)) {
      return parts[0];
    }

    return null;
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Tenant-Subdomain': getCurrentSubdomain() || ''
    };
  };

  // ============================================
  // EXISTING: loadUserData (UNCHANGED)
  // ============================================

  const loadUserData = async (userId, token) => {
    try {
      // If it's a MongoDB ObjectId (24 hex chars), don't make the API call
      if (userId && typeof userId === 'string' && userId.length === 24 && /^[0-9a-fA-F]{24}$/.test(userId)) {
        return null;
      }

      // Only make API call if userId is a number
      const numericUserId = Number(userId);
      if (isNaN(numericUserId)) {
        return null;
      }

      const response = await axios.get(`${getApiBaseUrl()}/api/auth/users/${numericUserId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success && response.data.user) {
        const userData = response.data.user;

        // Store stamp in localStorage if exists
        if (userData.preferences?.technicianStamp) {
          localStorage.setItem('technicianStamp', userData.preferences.technicianStamp);
        } else {
          localStorage.removeItem('technicianStamp');
        }

        return userData;
      }
    } catch (error) {
      // Silently handle error
      if (error.response?.status !== 400) {
        // no-op
      }
    }
    return null;
  };

  // ============================================
  // ✅ NEW: preserve redirect path helper
  // ============================================
  const preserveRedirectPath = () => {
    const currentPath = window.location.pathname + window.location.search;
    // Don't preserve login/reset pages
    if (
      currentPath &&
      currentPath !== '/' &&
      currentPath !== '/login' &&
      !currentPath.startsWith('/reset')
    ) {
      localStorage.setItem('redirectAfterLogin', currentPath);
    }
  };

  // ============================================
  // ✅ NEW: clear auth keys but KEEP redirectAfterLogin
  // ============================================
  const clearAuthDataKeepingRedirect = () => {
    const redirectPath = localStorage.getItem('redirectAfterLogin');

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userLabId');
    localStorage.removeItem('userData');
    localStorage.removeItem('currentTenant');
    localStorage.removeItem('technicianStamp');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('labInfo');
    localStorage.removeItem('userAddress');
    localStorage.removeItem('labName');
    localStorage.removeItem('labDetails');

    // Restore redirect path if it existed
    if (redirectPath) {
      localStorage.setItem('redirectAfterLogin', redirectPath);
    }
  };

  // ============================================
  // ✅ NEW: Silent refresh scheduler (sliding session)
  // Refreshes access token 2 min before it expires, so long as user is active.
  // If refresh token also expires (6h inactivity) → hard logout with redirect preserved.
  // ============================================
  const scheduleSilentRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    let expiresAt = 0;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      expiresAt = payload.exp * 1000;
    } catch (e) {
      return;
    }

    const now = Date.now();
    const msUntilExpiry = expiresAt - now;

    // Already expired → try refreshing immediately (or bail if refresh also gone)
    // Otherwise schedule 2 minutes before expiry
    const refreshIn = msUntilExpiry > 2 * 60 * 1000
      ? msUntilExpiry - 2 * 60 * 1000
      : 30 * 1000; // if less than 2 min left, retry in 30s

    refreshTimerRef.current = setTimeout(async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return;

      try {
        const { data } = await axios.post(
          `${getApiBaseUrl()}/api/auth/refresh`,
          { refreshToken }
        );

        if (data.success && data.accessToken) {
          localStorage.setItem('accessToken', data.accessToken);
          // Re-schedule for the freshly issued token
          scheduleSilentRefresh();
        }
      } catch (err) {
        // Refresh failed → token/refresh expired (inactive > 6h)
        // Preserve redirect + clear auth + redirect to login
        preserveRedirectPath();
        clearAuthDataKeepingRedirect();
        setIsAuthenticated(false);
        setUser(null);
        setTenant(null);

        // Only redirect if we're not already on login
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
    }, refreshIn);
  }, []);

  // ============================================
  // EXISTING: verifyToken on mount (UPDATED to schedule refresh)
  // ============================================
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        setLoading(false);
        setIsAuthenticated(false);
        return;
      }

      try {
        const apiBaseUrl = getApiBaseUrl();
        const response = await axios.get(`${apiBaseUrl}/api/auth/verify`, {
          headers: getAuthHeaders()
        });

        if (response.data) {
          const userData = response.data;

          // Store user ID in localStorage if not present
          if (userData.userId && !localStorage.getItem('userId')) {
            localStorage.setItem('userId', userData.userId);
          }

          // Load full user data including preferences - ONLY IF ID IS NUMERIC
          const userId = userData.userId || userData._id;
          const fullUserData = await loadUserData(userId, token);

          if (fullUserData) {
            setUser(fullUserData);
            if (fullUserData.preferences?.technicianStamp) {
              localStorage.setItem('technicianStamp', fullUserData.preferences.technicianStamp);
            }
          } else {
            setUser(userData);
          }

          setIsAuthenticated(true);

          // Load tenant from localStorage
          const storedTenant = localStorage.getItem('currentTenant');
          if (storedTenant) {
            setTenant(JSON.parse(storedTenant));
          }

          // ✅ Schedule silent refresh now that we know user is valid
          scheduleSilentRefresh();
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        // Token invalid/expired → try silent refresh once before giving up
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          try {
            const { data } = await axios.post(
              `${getApiBaseUrl()}/api/auth/refresh`,
              { refreshToken }
            );
            if (data.success && data.accessToken) {
              localStorage.setItem('accessToken', data.accessToken);
              // Retry verify with new token
              const retry = await axios.get(`${getApiBaseUrl()}/api/auth/verify`, {
                headers: {
                  'Authorization': `Bearer ${data.accessToken}`,
                  'Content-Type': 'application/json',
                  'X-Tenant-Subdomain': getCurrentSubdomain() || ''
                }
              });
              if (retry.data) {
                setUser(retry.data);
                setIsAuthenticated(true);
                const storedTenant = localStorage.getItem('currentTenant');
                if (storedTenant) setTenant(JSON.parse(storedTenant));
                scheduleSilentRefresh();
                setLoading(false);
                return;
              }
            }
          } catch (refreshErr) {
            // fall through to full clear
          }
        }

        // ✅ Preserve redirect path before clearing
        preserveRedirectPath();
        clearAuthDataKeepingRedirect();
        setIsAuthenticated(false);
        setUser(null);
        setTenant(null);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [scheduleSilentRefresh]);

  // ============================================
  // EXISTING: login (UPDATED to schedule silent refresh)
  // ============================================
  const login = async (token, userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('accessToken', token);

    if (userData.userId) {
      localStorage.setItem('userId', userData.userId);
    } else if (userData._id) {
      localStorage.setItem('userId', userData._id);
    }

    if (userData.role) {
      localStorage.setItem('userRole', userData.role);
    }

    if (userData.labId) {
      localStorage.setItem('userLabId', userData.labId);
    }

    if (userData.preferences?.technicianStamp) {
      localStorage.setItem('technicianStamp', userData.preferences.technicianStamp);
    } else {
      localStorage.removeItem('technicianStamp');
    }

    // Load full user data - ONLY IF ID IS NUMERIC
    const userId = userData.userId || userData._id;
    const numericUserId = Number(userId);
    if (!isNaN(numericUserId) && userId && typeof userId !== 'string') {
      const fullUserData = await loadUserData(userId, token);
      if (fullUserData) {
        setUser(fullUserData);
      }
    } else if (userId && typeof userId === 'string' && userId.length === 24 && /^[0-9a-fA-F]{24}$/.test(userId)) {
      // MongoDB ObjectId, skip
    }

    // ✅ Schedule silent refresh after login
    scheduleSilentRefresh();
  };

  // ============================================
  // EXISTING: logout (UPDATED to preserve redirect)
  // ============================================
  const logout = () => {
    // ✅ Save where the user is now, so after re-login they land back here
    preserveRedirectPath();

    setUser(null);
    setTenant(null);
    setIsAuthenticated(false);

    // Clear silent refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Clear auth data but KEEP redirectAfterLogin
    clearAuthDataKeepingRedirect();
  };

  // ============================================
  // EXISTING: hasTenantAccess (UNCHANGED)
  // ============================================
  const hasTenantAccess = () => {
    if (!user) return false;

    const userLabId = localStorage.getItem('userLabId');

    if (user.role === 'Admin') {
      return true;
    }

    const storedTenant = localStorage.getItem('currentTenant');
    if (storedTenant) {
      const tenantData = JSON.parse(storedTenant);
      return tenantData.labId === userLabId;
    }

    return true;
  };

  const value = {
    user,
    tenant,
    loading,
    isAuthenticated,
    login,
    logout,
    hasTenantAccess,
    getApiBaseUrl,
    getAuthHeaders,
    getCurrentSubdomain
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;